import * as commonmark from 'commonmark';
import DOMPurify from 'dompurify';
// Provides an HTML renderer for markdown conforming to the [CommonMark](https://spec.commonmark.org) specification
// This implementation uses the reference JavaScript implementation of the CommonMark parser
// For full details see the [CommonMark GitHub](https://github.com/commonmark/commonmark.js) pages.
export class CommonMarkRenderer {
    constructor(window = undefined, // this allows us to inject a window to test the sanitiser
    parserOptions = {}, rendererOptions = {}) {
        this.parser = new commonmark.Parser(parserOptions);
        this.renderer = new commonmark.HtmlRenderer(rendererOptions);
        this.sanitiser = DOMPurify(window);
    }
    // Returns an HTML string representation of the markdown.
    // The HTML returned is sanitized against XSS attacks using [DOMPurify](https://github.com/cure53/DOMPurify)
    // and is safe to use as the HTML content of `dangerouslySetInnerHTML` properties.
    renderHtml(markdown) {
        const ast = this.parser.parse(markdown);
        const html = this.renderer.render(ast);
        // In browser environment, preserve target="_blank" attributes from markdown
        // and ensure they also have rel="noopener noreferrer" for security
        if (typeof DOMParser !== 'undefined') {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const linksWithTarget = Array.from(doc.querySelectorAll('a[target="_blank"]')).map((link) => link.getAttribute('href'));
            this.sanitiser.addHook('afterSanitizeAttributes', (node) => {
                if (node.tagName === 'A' && linksWithTarget.includes(node.getAttribute('href'))) {
                    node.setAttribute('target', '_blank');
                    node.setAttribute('rel', 'noopener noreferrer');
                }
            });
        }
        // full list of tags allowed https://github.com/cure53/DOMPurify/blob/main/src/tags.js
        // we remove some extra ones we don't want here
        const sanitized = this.sanitiser.sanitize(html, {
            FORBID_TAGS: [
                'style',
                'img',
                'form',
                'field',
                'input',
                'button',
                'select',
                'textarea',
                'canvas',
                'audio',
                'video',
                'source',
                'picture',
            ],
            USE_PROFILES: { html: true },
            ADD_ATTR: ['target', 'rel'],
        });
        // Clean up hooks to avoid affecting subsequent sanitizations
        if (typeof DOMParser !== 'undefined') {
            this.sanitiser.removeAllHooks();
        }
        return sanitized;
    }
}
